import React, { useEffect, useState } from 'react';
import { SearchIcon, FilterIcon, XCircleIcon, ClockIcon, CheckCircleIcon, AlertCircleIcon, RefreshCwIcon, EyeIcon, CalendarIcon, ArrowUpDownIcon, BuildingIcon, UserIcon, PhoneIcon, MailIcon, MapPinIcon, InfoIcon, ChevronDownIcon, ChevronUpIcon, ArrowLeftIcon, DownloadIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
import Modal from '../../components/ui/Modal';
// Mock merchants data
const merchantsData = [{
  id: 'M001',
  name: 'Global Electronics',
  businessType: 'Electronics',
  status: 'active',
  contactPerson: 'Michael Lee',
  email: 'michael@globalelectronics.com',
  phone: '+1 (555) 123-4567',
  address: '123 Main St, Downtown, NY 10001',
  registrationDate: '2022-03-15T10:20:00Z',
  totalTransactions: 1256,
  successRate: 98.2
}, {
  id: 'M002',
  name: 'Fashion Forward',
  businessType: 'Apparel',
  status: 'active',
  contactPerson: 'Sophia Martinez',
  email: 'sophia@fashionforward.com',
  phone: '+1 (555) 987-6543',
  address: '456 Fashion Ave, Midtown, NY 10018',
  registrationDate: '2022-05-22T14:15:00Z',
  totalTransactions: 875,
  successRate: 97.5
}, {
  id: 'M003',
  name: 'Gourmet Coffee Shop',
  businessType: 'Food & Beverage',
  status: 'inactive',
  contactPerson: 'James Wilson',
  email: 'james@gourmetcoffee.com',
  phone: '+1 (555) 456-7890',
  address: '789 Café Lane, SoHo, NY 10013',
  registrationDate: '2022-01-10T08:20:00Z',
  totalTransactions: 1542,
  successRate: 96.8
}, {
  id: 'M004',
  name: 'Premium Phones',
  businessType: 'Electronics',
  status: 'active',
  contactPerson: 'Emily Brown',
  email: 'emily@premiumphones.com',
  phone: '+1 (555) 234-5678',
  address: '101 Tech Plaza, Financial District, NY 10005',
  registrationDate: '2022-08-05T10:15:00Z',
  totalTransactions: 652,
  successRate: 99.1
}, {
  id: 'M005',
  name: 'Seaside Resort',
  businessType: 'Hospitality',
  status: 'active',
  contactPerson: 'Daniel Smith',
  email: 'daniel@seasideresort.com',
  phone: '+1 (555) 876-5432',
  address: '202 Oceanview Drive, Hamptons, NY 11937',
  registrationDate: '2022-06-15T12:20:00Z',
  totalTransactions: 987,
  successRate: 98.7
}];
// Mock transactions data
const generateTransactions = (merchantId: string) => {
  const statuses = ['completed', 'pending', 'rejected', 'refunded'];
  const methods = ['QR Code', 'Card Payment', 'Bank Transfer', 'Mobile Wallet'];
  const transactions = [];
  // Generate between 15-25 transactions
  const count = Math.floor(Math.random() * 10) + 15;
  for (let i = 0; i < count; i++) {
    const status = statuses[Math.floor(Math.random() * statuses.length)];
    // Make sure we have some rejected transactions
    const finalStatus = i < 3 ? 'rejected' : status;
    // Generate date within last 30 days
    const date = new Date();
    date.setDate(date.getDate() - Math.floor(Math.random() * 30));
    date.setHours(Math.floor(Math.random() * 24), Math.floor(Math.random() * 60));
    transactions.push({
      id: `TX${merchantId.substring(1)}${i.toString().padStart(3, '0')}`,
      merchantId,
      amount: Math.floor(Math.random() * 1000000) / 100,
      status: finalStatus,
      method: methods[Math.floor(Math.random() * methods.length)],
      date: date.toISOString(),
      customer: `Customer ${i + 1}`,
      reference: `REF-${merchantId}-${i}`,
      description: `Transaction for ${merchantId}`,
      rejectionReason: finalStatus === 'rejected' ? ['Insufficient funds', 'Suspicious activity', 'Payment limit exceeded', 'Card expired'][Math.floor(Math.random() * 4)] : null
    });
  }
  // Sort by date descending
  return transactions.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
};
// Transaction type
interface Transaction {
  id: string;
  merchantId: string;
  amount: number;
  status: string;
  method: string;
  date: string;
  customer: string;
  reference: string;
  description: string;
  rejectionReason: string | null;
}
// Merchant type
interface Merchant {
  id: string;
  name: string;
  businessType: string;
  status: string;
  contactPerson: string;
  email: string;
  phone: string;
  address: string;
  registrationDate: string;
  totalTransactions: number;
  successRate: number;
}
const MerchantPaymentMonitor: React.FC = () => {
  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [merchants, setMerchants] = useState<Merchant[]>(merchantsData);
  const [filteredMerchants, setFilteredMerchants] = useState<Merchant[]>(merchantsData);
  const [selectedMerchant, setSelectedMerchant] = useState<Merchant | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [filteredTransactions, setFilteredTransactions] = useState<Transaction[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showMerchantDetails, setShowMerchantDetails] = useState(false);
  const [showTransactionDetails, setShowTransactionDetails] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  // Filter states
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  const [methodFilter, setMethodFilter] = useState('');
  const [onlyRejected, setOnlyRejected] = useState(false);
  // Sorting
  const [sortField, setSortField] = useState<string>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  // When a merchant is selected, load their transactions
  useEffect(() => {
    if (selectedMerchant) {
      const merchantTransactions = generateTransactions(selectedMerchant.id);
      setTransactions(merchantTransactions);
      setFilteredTransactions(merchantTransactions);
    } else {
      setTransactions([]);
      setFilteredTransactions([]);
    }
  }, [selectedMerchant]);
  // Filter merchants based on search term
  useEffect(() => {
    if (searchTerm) {
      const filtered = merchants.filter(merchant => merchant.name.toLowerCase().includes(searchTerm.toLowerCase()) || merchant.id.toLowerCase().includes(searchTerm.toLowerCase()) || merchant.businessType.toLowerCase().includes(searchTerm.toLowerCase()) || merchant.contactPerson.toLowerCase().includes(searchTerm.toLowerCase()));
      setFilteredMerchants(filtered);
    } else {
      setFilteredMerchants(merchants);
    }
  }, [searchTerm, merchants]);
  // Apply transaction filters
  const applyTransactionFilters = () => {
    let filtered = [...transactions];
    // Status filter
    if (statusFilter) {
      filtered = filtered.filter(t => t.status === statusFilter);
    }
    // Only rejected filter (overrides status filter)
    if (onlyRejected) {
      filtered = filtered.filter(t => t.status === 'rejected');
    }
    // Method filter
    if (methodFilter) {
      filtered = filtered.filter(t => t.method === methodFilter);
    }
    // Date range filter
    if (dateFromFilter) {
      const fromDate = new Date(dateFromFilter);
      filtered = filtered.filter(t => new Date(t.date) >= fromDate);
    }
    if (dateToFilter) {
      const toDate = new Date(dateToFilter);
      toDate.setHours(23, 59, 59, 999); // End of day
      filtered = filtered.filter(t => new Date(t.date) <= toDate);
    }
    // Apply sorting
    filtered = filtered.sort((a, b) => {
      const fieldA = a[sortField as keyof Transaction];
      const fieldB = b[sortField as keyof Transaction];
      if (typeof fieldA === 'string' && typeof fieldB === 'string') {
        return sortDirection === 'asc' ? fieldA.localeCompare(fieldB) : fieldB.localeCompare(fieldA);
      } else {
        // Assuming numeric comparison
        return sortDirection === 'asc' ? (fieldA as number) - (fieldB as number) : (fieldB as number) - (fieldA as number);
      }
    });
    setFilteredTransactions(filtered);
  };
  // Apply filters when they change
  useEffect(() => {
    applyTransactionFilters();
  }, [statusFilter, dateFromFilter, dateToFilter, methodFilter, onlyRejected, sortField, sortDirection, transactions]);
  // Reset filters
  const resetFilters = () => {
    setStatusFilter('');
    setDateFromFilter('');
    setDateToFilter('');
    setMethodFilter('');
    setOnlyRejected(false);
  };
  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };
  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  // Format time
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };
  // Format date and time
  const formatDateTime = (dateString: string) => {
    return `${formatDate(dateString)} at ${formatTime(dateString)}`;
  };
  // Handle sort
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300" data-id="element-4408">
            <CheckCircleIcon className="w-3 h-3 mr-1" data-id="element-4409" />
            Completed
          </span>;
      case 'pending':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300" data-id="element-4410">
            <ClockIcon className="w-3 h-3 mr-1" data-id="element-4411" />
            Pending
          </span>;
      case 'rejected':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300" data-id="element-4412">
            <XCircleIcon className="w-3 h-3 mr-1" data-id="element-4413" />
            Rejected
          </span>;
      case 'refunded':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300" data-id="element-4414">
            <RefreshCwIcon className="w-3 h-3 mr-1" data-id="element-4415" />
            Refunded
          </span>;
      case 'active':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300" data-id="element-4416">
            <CheckCircleIcon className="w-3 h-3 mr-1" data-id="element-4417" />
            Active
          </span>;
      case 'inactive':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300" data-id="element-4418">
            <XCircleIcon className="w-3 h-3 mr-1" data-id="element-4419" />
            Inactive
          </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300" data-id="element-4420">
            {status}
          </span>;
    }
  };
  // View merchant details
  const viewMerchantDetails = (merchant: Merchant) => {
    setSelectedMerchant(merchant);
    setShowMerchantDetails(true);
  };
  // View transaction details
  const viewTransactionDetails = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setShowTransactionDetails(true);
  };
  // Select merchant to view transactions
  const selectMerchant = (merchant: Merchant) => {
    setSelectedMerchant(merchant);
    resetFilters();
  };
  // Clear selected merchant
  const clearSelectedMerchant = () => {
    setSelectedMerchant(null);
    setTransactions([]);
    setFilteredTransactions([]);
  };
  return <div className="space-y-6" data-id="element-4421">
      <div data-id="element-4422">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-4423">
          Merchant Payment Monitor
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-4424">
          Search merchants, view their payment details and transaction history
        </p>
      </div>
      {/* Merchant Search & Selection */}
      {!selectedMerchant ? <>
          <div className="relative" data-id="element-4425">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-4426">
              <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-4427" />
            </div>
            <input type="text" placeholder="Search merchants by name, ID, or business type..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800" data-id="element-4428" />
          </div>
          <Card data-id="element-4429">
            <div className="overflow-x-auto" data-id="element-4430">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-4431">
                <thead className="bg-gray-50 dark:bg-gray-700" data-id="element-4432">
                  <tr data-id="element-4433">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-4434">
                      Merchant
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-4435">
                      Business Type
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-4436">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-4437">
                      Total Transactions
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-4438">
                      Success Rate
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-4439">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-4440">
                  {filteredMerchants.map(merchant => <tr key={merchant.id} className="hover:bg-gray-50 dark:hover:bg-gray-750" data-id="element-4441">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-4442">
                        <div className="flex items-center" data-id="element-4443">
                          <div className="flex-shrink-0 h-10 w-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center" data-id="element-4444">
                            <BuildingIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" data-id="element-4445" />
                          </div>
                          <div className="ml-4" data-id="element-4446">
                            <div className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-4447">
                              {merchant.name}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4448">
                              {merchant.id}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-4449">
                        {merchant.businessType}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-4450">
                        {getStatusBadge(merchant.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-4451">
                        {merchant.totalTransactions.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-4452">
                        <div className="flex items-center" data-id="element-4453">
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 mr-2 max-w-[100px]" data-id="element-4454">
                            <div className={`h-2.5 rounded-full ${merchant.successRate >= 98 ? 'bg-green-500' : merchant.successRate >= 95 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{
                        width: `${merchant.successRate}%`
                      }} data-id="element-4455"></div>
                          </div>
                          <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4456">
                            {merchant.successRate}%
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" data-id="element-4457">
                        <div className="flex space-x-2" data-id="element-4458">
                          <button onClick={() => viewMerchantDetails(merchant)} className="text-purple-600 hover:text-purple-900 dark:text-purple-400 dark:hover:text-purple-300" title="View Details" data-id="element-4459">
                            <InfoIcon className="h-5 w-5" data-id="element-4460" />
                          </button>
                          <button onClick={() => selectMerchant(merchant)} className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300" title="View Transactions" data-id="element-4461">
                            <EyeIcon className="h-5 w-5" data-id="element-4462" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
                  {filteredMerchants.length === 0 && <tr data-id="element-4463">
                      <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400" data-id="element-4464">
                        No merchants found matching your search.
                      </td>
                    </tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </> : <>
          {/* Selected Merchant & Transactions View */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between" data-id="element-4465">
            <div data-id="element-4466">
              <button onClick={clearSelectedMerchant} className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mb-2" data-id="element-4467">
                <ArrowLeftIcon className="h-4 w-4 mr-1" data-id="element-4468" />
                Back to Merchant List
              </button>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center" data-id="element-4469">
                {selectedMerchant.name}
                <span className="ml-2" data-id="element-4470">
                  {getStatusBadge(selectedMerchant.status)}
                </span>
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4471">
                {selectedMerchant.id} • {selectedMerchant.businessType}
              </p>
            </div>
            <div className="mt-3 sm:mt-0 flex flex-wrap gap-2" data-id="element-4472">
              <button onClick={() => viewMerchantDetails(selectedMerchant)} className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-4473">
                <InfoIcon className="h-4 w-4 mr-2" data-id="element-4474" />
                Merchant Details
              </button>
              <button onClick={() => setShowFilters(!showFilters)} className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-4475">
                <FilterIcon className="h-4 w-4 mr-2" data-id="element-4476" />
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </button>
              <button className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-4477">
                <DownloadIcon className="h-4 w-4 mr-2" data-id="element-4478" />
                Export
              </button>
            </div>
          </div>
          {/* Transaction Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-4479">
            <Card className="bg-white dark:bg-gray-800" data-id="element-4480">
              <div className="flex items-center" data-id="element-4481">
                <div className="p-3 rounded-full bg-purple-100 dark:bg-purple-900/30 mr-4" data-id="element-4482">
                  <ArrowUpDownIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" data-id="element-4483" />
                </div>
                <div data-id="element-4484">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-4485">
                    Total Transactions
                  </p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white" data-id="element-4486">
                    {transactions.length}
                  </p>
                </div>
              </div>
            </Card>
            <Card className="bg-white dark:bg-gray-800" data-id="element-4487">
              <div className="flex items-center" data-id="element-4488">
                <div className="p-3 rounded-full bg-green-100 dark:bg-green-900/30 mr-4" data-id="element-4489">
                  <CheckCircleIcon className="h-6 w-6 text-green-600 dark:text-green-400" data-id="element-4490" />
                </div>
                <div data-id="element-4491">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-4492">
                    Completed
                  </p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white" data-id="element-4493">
                    {transactions.filter(t => t.status === 'completed').length}
                  </p>
                </div>
              </div>
            </Card>
            <Card className="bg-white dark:bg-gray-800" data-id="element-4494">
              <div className="flex items-center" data-id="element-4495">
                <div className="p-3 rounded-full bg-red-100 dark:bg-red-900/30 mr-4" data-id="element-4496">
                  <XCircleIcon className="h-6 w-6 text-red-600 dark:text-red-400" data-id="element-4497" />
                </div>
                <div data-id="element-4498">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-4499">
                    Rejected
                  </p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white" data-id="element-4500">
                    {transactions.filter(t => t.status === 'rejected').length}
                  </p>
                </div>
              </div>
            </Card>
            <Card className="bg-white dark:bg-gray-800" data-id="element-4501">
              <div className="flex items-center" data-id="element-4502">
                <div className="p-3 rounded-full bg-yellow-100 dark:bg-yellow-900/30 mr-4" data-id="element-4503">
                  <ClockIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" data-id="element-4504" />
                </div>
                <div data-id="element-4505">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-4506">
                    Pending
                  </p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white" data-id="element-4507">
                    {transactions.filter(t => t.status === 'pending').length}
                  </p>
                </div>
              </div>
            </Card>
          </div>
          {/* Transaction Filters */}
          {showFilters && <Card data-id="element-4508">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-4509">
                <div data-id="element-4510">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4511">
                    Status
                  </label>
                  <select value={statusFilter} onChange={e => {
              setStatusFilter(e.target.value);
              if (e.target.value !== 'rejected') {
                setOnlyRejected(false);
              }
            }} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" disabled={onlyRejected} data-id="element-4512">
                    <option value="" data-id="element-4513">All Statuses</option>
                    <option value="completed" data-id="element-4514">Completed</option>
                    <option value="pending" data-id="element-4515">Pending</option>
                    <option value="rejected" data-id="element-4516">Rejected</option>
                    <option value="refunded" data-id="element-4517">Refunded</option>
                  </select>
                </div>
                <div data-id="element-4518">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4519">
                    Payment Method
                  </label>
                  <select value={methodFilter} onChange={e => setMethodFilter(e.target.value)} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-4520">
                    <option value="" data-id="element-4521">All Methods</option>
                    <option value="QR Code" data-id="element-4522">QR Code</option>
                    <option value="Card Payment" data-id="element-4523">Card Payment</option>
                    <option value="Bank Transfer" data-id="element-4524">Bank Transfer</option>
                    <option value="Mobile Wallet" data-id="element-4525">Mobile Wallet</option>
                  </select>
                </div>
                <div data-id="element-4526">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4527">
                    Date From
                  </label>
                  <div className="relative" data-id="element-4528">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-4529">
                      <CalendarIcon className="h-5 w-5 text-gray-400" data-id="element-4530" />
                    </div>
                    <input type="date" value={dateFromFilter} onChange={e => setDateFromFilter(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800" data-id="element-4531" />
                  </div>
                </div>
                <div data-id="element-4532">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4533">
                    Date To
                  </label>
                  <div className="relative" data-id="element-4534">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-4535">
                      <CalendarIcon className="h-5 w-5 text-gray-400" data-id="element-4536" />
                    </div>
                    <input type="date" value={dateToFilter} onChange={e => setDateToFilter(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800" data-id="element-4537" />
                  </div>
                </div>
                <div className="flex items-center" data-id="element-4538">
                  <input id="only-rejected" type="checkbox" checked={onlyRejected} onChange={e => {
              setOnlyRejected(e.target.checked);
              if (e.target.checked) {
                setStatusFilter('rejected');
              }
            }} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded" data-id="element-4539" />
                  <label htmlFor="only-rejected" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-4540">
                    Show only rejected transactions
                  </label>
                </div>
              </div>
              <div className="mt-4 flex justify-end space-x-3" data-id="element-4541">
                <button onClick={resetFilters} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-4542">
                  Reset
                </button>
                <button onClick={() => setShowFilters(false)} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-purple-600 hover:bg-purple-700" data-id="element-4543">
                  Apply Filters
                </button>
              </div>
            </Card>}
          {/* Transactions Table */}
          <Card data-id="element-4544">
            <div className="mb-4 flex items-center justify-between" data-id="element-4545">
              <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4546">
                Showing {filteredTransactions.length} of {transactions.length}{' '}
                transactions
              </div>
            </div>
            <div className="overflow-x-auto" data-id="element-4547">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-4548">
                <thead className="bg-gray-50 dark:bg-gray-700" data-id="element-4549">
                  <tr data-id="element-4550">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('id')} data-id="element-4551">
                      <div className="flex items-center" data-id="element-4552">
                        Transaction ID
                        {sortField === 'id' && (sortDirection === 'asc' ? <ChevronUpIcon className="ml-1 h-4 w-4" data-id="element-4553" /> : <ChevronDownIcon className="ml-1 h-4 w-4" data-id="element-4554" />)}
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('date')} data-id="element-4555">
                      <div className="flex items-center" data-id="element-4556">
                        Date & Time
                        {sortField === 'date' && (sortDirection === 'asc' ? <ChevronUpIcon className="ml-1 h-4 w-4" data-id="element-4557" /> : <ChevronDownIcon className="ml-1 h-4 w-4" data-id="element-4558" />)}
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('amount')} data-id="element-4559">
                      <div className="flex items-center" data-id="element-4560">
                        Amount
                        {sortField === 'amount' && (sortDirection === 'asc' ? <ChevronUpIcon className="ml-1 h-4 w-4" data-id="element-4561" /> : <ChevronDownIcon className="ml-1 h-4 w-4" data-id="element-4562" />)}
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('method')} data-id="element-4563">
                      <div className="flex items-center" data-id="element-4564">
                        Method
                        {sortField === 'method' && (sortDirection === 'asc' ? <ChevronUpIcon className="ml-1 h-4 w-4" data-id="element-4565" /> : <ChevronDownIcon className="ml-1 h-4 w-4" data-id="element-4566" />)}
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('status')} data-id="element-4567">
                      <div className="flex items-center" data-id="element-4568">
                        Status
                        {sortField === 'status' && (sortDirection === 'asc' ? <ChevronUpIcon className="ml-1 h-4 w-4" data-id="element-4569" /> : <ChevronDownIcon className="ml-1 h-4 w-4" data-id="element-4570" />)}
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-4571">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-4572">
                  {filteredTransactions.map(transaction => <tr key={transaction.id} className={`hover:bg-gray-50 dark:hover:bg-gray-750 ${transaction.status === 'rejected' ? 'bg-red-50 dark:bg-red-900/10' : ''}`} data-id="element-4573">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white" data-id="element-4574">
                        {transaction.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-4575">
                        <div data-id="element-4576">{formatDate(transaction.date)}</div>
                        <div className="text-xs text-gray-400 dark:text-gray-500" data-id="element-4577">
                          {formatTime(transaction.date)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-4578">
                        {formatCurrency(transaction.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-4579">
                        {transaction.method}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-4580">
                        {getStatusBadge(transaction.status)}
                        {transaction.status === 'rejected' && transaction.rejectionReason && <div className="text-xs text-red-600 dark:text-red-400 mt-1" data-id="element-4581">
                              {transaction.rejectionReason}
                            </div>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-4582">
                        <button onClick={() => viewTransactionDetails(transaction)} className="text-purple-600 hover:text-purple-900 dark:text-purple-400 dark:hover:text-purple-300" data-id="element-4583">
                          <EyeIcon className="h-5 w-5" data-id="element-4584" />
                        </button>
                      </td>
                    </tr>)}
                  {filteredTransactions.length === 0 && <tr data-id="element-4585">
                      <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400" data-id="element-4586">
                        No transactions found matching your filters.
                      </td>
                    </tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </>}
      {/* Merchant Details Modal */}
      <Modal isOpen={showMerchantDetails} onClose={() => setShowMerchantDetails(false)} title="Merchant Details" size="lg" data-id="element-4587">
        {selectedMerchant && <div className="space-y-6" data-id="element-4588">
            <div className="flex items-center" data-id="element-4589">
              <div className="p-3 rounded-full bg-purple-100 dark:bg-purple-900/30 mr-4" data-id="element-4590">
                <BuildingIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" data-id="element-4591" />
              </div>
              <div data-id="element-4592">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-4593">
                  {selectedMerchant.name}
                </h3>
                <div className="flex items-center mt-1" data-id="element-4594">
                  {getStatusBadge(selectedMerchant.status)}
                  <span className="mx-2 text-gray-500 dark:text-gray-400" data-id="element-4595">
                    •
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4596">
                    {selectedMerchant.businessType}
                  </span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4597">
              <div data-id="element-4598">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3" data-id="element-4599">
                  Contact Information
                </h4>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-3" data-id="element-4600">
                  <div className="flex items-start" data-id="element-4601">
                    <UserIcon className="h-5 w-5 text-gray-400 mt-0.5 mr-3 flex-shrink-0" data-id="element-4602" />
                    <div data-id="element-4603">
                      <div className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-4604">
                        Contact Person
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400" data-id="element-4605">
                        {selectedMerchant.contactPerson}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-4606">
                    <MailIcon className="h-5 w-5 text-gray-400 mt-0.5 mr-3 flex-shrink-0" data-id="element-4607" />
                    <div data-id="element-4608">
                      <div className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-4609">
                        Email
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400" data-id="element-4610">
                        {selectedMerchant.email}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-4611">
                    <PhoneIcon className="h-5 w-5 text-gray-400 mt-0.5 mr-3 flex-shrink-0" data-id="element-4612" />
                    <div data-id="element-4613">
                      <div className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-4614">
                        Phone
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400" data-id="element-4615">
                        {selectedMerchant.phone}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-4616">
                    <MapPinIcon className="h-5 w-5 text-gray-400 mt-0.5 mr-3 flex-shrink-0" data-id="element-4617" />
                    <div data-id="element-4618">
                      <div className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-4619">
                        Address
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400" data-id="element-4620">
                        {selectedMerchant.address}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div data-id="element-4621">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3" data-id="element-4622">
                  Merchant Statistics
                </h4>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-3" data-id="element-4623">
                  <div className="flex items-center justify-between" data-id="element-4624">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-4625">
                      Registration Date:
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-4626">
                      {formatDate(selectedMerchant.registrationDate)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-4627">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-4628">
                      Total Transactions:
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-4629">
                      {selectedMerchant.totalTransactions.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-4630">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-4631">
                      Success Rate:
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-4632">
                      {selectedMerchant.successRate}%
                    </span>
                  </div>
                  <div data-id="element-4633">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-4634">
                      Transaction Success:
                    </span>
                    <div className="mt-2" data-id="element-4635">
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5" data-id="element-4636">
                        <div className={`h-2.5 rounded-full ${selectedMerchant.successRate >= 98 ? 'bg-green-500' : selectedMerchant.successRate >= 95 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{
                      width: `${selectedMerchant.successRate}%`
                    }} data-id="element-4637"></div>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-4638">
                        <span data-id="element-4639">0%</span>
                        <span data-id="element-4640">50%</span>
                        <span data-id="element-4641">100%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4" data-id="element-4642">
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2" data-id="element-4643">
                Quick Actions
              </h4>
              <div className="flex flex-wrap gap-2" data-id="element-4644">
                <button onClick={() => {
              setShowMerchantDetails(false);
              if (!selectedMerchant) {
                selectMerchant(selectedMerchant);
              }
            }} className="px-3 py-1 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 rounded-md text-sm" data-id="element-4645">
                  View Transactions
                </button>
                <button className="px-3 py-1 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded-md text-sm" data-id="element-4646">
                  Download Reports
                </button>
                <button className="px-3 py-1 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 rounded-md text-sm" data-id="element-4647">
                  Contact Merchant
                </button>
              </div>
            </div>
          </div>}
      </Modal>
      {/* Transaction Details Modal */}
      <Modal isOpen={showTransactionDetails} onClose={() => setShowTransactionDetails(false)} title="Transaction Details" size="lg" data-id="element-4648">
        {selectedTransaction && <div className="space-y-6" data-id="element-4649">
            <div className="flex items-center justify-between" data-id="element-4650">
              <div data-id="element-4651">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-4652">
                  {selectedTransaction.id}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4653">
                  {formatDateTime(selectedTransaction.date)}
                </p>
              </div>
              <div data-id="element-4654">{getStatusBadge(selectedTransaction.status)}</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4655">
              <div data-id="element-4656">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3" data-id="element-4657">
                  Transaction Information
                </h4>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-3" data-id="element-4658">
                  <div className="flex justify-between" data-id="element-4659">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-4660">
                      Amount:
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-4661">
                      {formatCurrency(selectedTransaction.amount)}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-4662">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-4663">
                      Payment Method:
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-4664">
                      {selectedTransaction.method}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-4665">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-4666">
                      Reference:
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-4667">
                      {selectedTransaction.reference}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-4668">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-4669">
                      Description:
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-4670">
                      {selectedTransaction.description}
                    </span>
                  </div>
                </div>
              </div>
              <div data-id="element-4671">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3" data-id="element-4672">
                  Additional Details
                </h4>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-3" data-id="element-4673">
                  <div className="flex justify-between" data-id="element-4674">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-4675">
                      Customer:
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-4676">
                      {selectedTransaction.customer}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-4677">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-4678">
                      Merchant ID:
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-4679">
                      {selectedTransaction.merchantId}
                    </span>
                  </div>
                  {selectedTransaction.status === 'rejected' && selectedTransaction.rejectionReason && <div className="pt-2 mt-2 border-t border-gray-200 dark:border-gray-700" data-id="element-4680">
                        <div className="text-sm font-medium text-red-600 dark:text-red-400" data-id="element-4681">
                          Rejection Reason:
                        </div>
                        <div className="mt-1 text-sm text-gray-600 dark:text-gray-400 bg-red-50 dark:bg-red-900/20 p-2 rounded border border-red-100 dark:border-red-800" data-id="element-4682">
                          {selectedTransaction.rejectionReason}
                        </div>
                      </div>}
                </div>
              </div>
            </div>
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4" data-id="element-4683">
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2" data-id="element-4684">
                Actions
              </h4>
              <div className="flex flex-wrap gap-2" data-id="element-4685">
                <button className="px-3 py-1 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 rounded-md text-sm" data-id="element-4686">
                  Download Receipt
                </button>
                {selectedTransaction.status === 'rejected' && <button className="px-3 py-1 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 rounded-md text-sm" data-id="element-4687">
                    Retry Transaction
                  </button>}
                <button className="px-3 py-1 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded-md text-sm" data-id="element-4688">
                  View Full Details
                </button>
              </div>
            </div>
          </div>}
      </Modal>
    </div>;
};
export default MerchantPaymentMonitor;